/**
 * Living-bar section chrome skins — human-chosen on the dashboard Widget Design
 * page (not LLM-generated). Renders current/next section questions above the
 * sticky bottom bar.
 *
 * Pure module (no zod) so the widget snippet (bh.js) can import skins safely.
 * Zod schemas live in ./living-bar-schema.ts for dashboard / API.
 */

export const LIVING_BAR_SKINS = [
  // First in the list because it is the one a tenant with a generated design
  // should find first. It is not a hand-drawn look: it is the bar the AI
  // designer produced from the site's own screenshots.
  "ai_styled",
  "glass",
  "constellation",
  "radar",
  "split_theater",
  "on_this_page",
  "side_rail",
  "side_theater",
  "clinical_glass",
  "graphite",
  "ember",
  "deep_field",
  // Tail of the list on purpose: both are fun, neither is a working default.
  // Order here IS the picker's order — a site owner should meet the styles
  // they would actually ship before the novelties.
  "sticky_notes",
  "command_line",
] as const;
export type LivingBarSkin = (typeof LIVING_BAR_SKINS)[number];

/** Default when unset / older tenants — the first section skin we shipped.
 *  Position in LIVING_BAR_SKINS is display order only; this is the default. */
export const DEFAULT_LIVING_BAR_SKIN: LivingBarSkin = "sticky_notes";

/**
 * Glass-capsule family — ONE geometry, four palettes (floating question
 * stack, capsule, dedicated pill row, fit-aware selection, no section
 * labels, nothing truncates). Clinical Glass discovered the shape; the
 * others are its brand palettes. Anything that asks "is this the capsule
 * shape?" must use this, never an equality check against one member —
 * that is how three of the four silently lose their behaviour.
 */
export const GLASS_CAPSULE_SKINS: readonly LivingBarSkin[] = [
  "clinical_glass",
  "graphite",
  "ember",
  "deep_field",
];

export function isGlassCapsuleSkin(skin: LivingBarSkin | null | undefined): boolean {
  // Nullable by design: callers hold `livingBarSkin` before config lands, and
  // the equality checks this replaced were null-tolerant.
  return skin != null && GLASS_CAPSULE_SKINS.includes(skin);
}

/**
 * Capsule CTA copy per member. "Ask privately" is CLINICAL GLASS's identity
 * — the privacy reassurance for medical/trust-sensitive tenants. The brand
 * palettes are ordinary commercial surfaces and say "Get answers" (Fadi
 * 2026-08-05: "only the clinical one should say ask privately"). The family
 * predicate converted the old per-skin CTA check into family-wide by
 * accident; copy is per-member and lives HERE so bubble.tsx stays free of
 * member equality checks (the family test bans them there).
 */
export function glassCapsuleCtaLabel(skin: LivingBarSkin): string {
  return skin === "clinical_glass" ? "Ask privately" : "Get answers";
}

/** Palette modifier within the glass-capsule family ("" = Clinical Glass). */
export function glassCapsulePalette(skin: LivingBarSkin | null | undefined): string {
  switch (skin) {
    case "graphite":
      return "graphite";
    case "ember":
      return "ember";
    case "deep_field":
      return "deepfield";
    default:
      return "";
  }
}

/**
 * The AI-generated bar. Unlike every other member this carries NO hand-drawn
 * section chrome: the widget renders its plain capsule wearing the generated
 * palette (the `--bhjs-*` theme the designer wrote) and the generated
 * placeholder copy. Callers that stamp section-chrome classes must exclude it
 * — the whole point is that nothing is stamped over the generated look.
 *
 * It also has a precondition no other skin has: a design must exist. With no
 * generated design the bar falls back to stock colors, so the dashboard blocks
 * activation until the AI designer has run.
 */
export function isAiStyledSkin(skin: LivingBarSkin | null | undefined): boolean {
  return skin === "ai_styled";
}

/**
 * The look of a questions-only surface (inline `nudges` mode, or the lower bar
 * showing pills alone). It is the question pills with nothing else, so it can
 * only borrow a skin that HAS pills of that kind: the glass-capsule family
 * keeps its palette, and every other skin — the AI style included — wears the
 * AI style, which takes the generated design's own colors (stock white when
 * there is no design). The section skins' chrome (notes, radar, theater) has
 * no pills-alone form to fall back to.
 */
export function nudgesSurfaceLook(skin: LivingBarSkin | null | undefined): { ai: boolean; palette: string } {
  if (isGlassCapsuleSkin(skin)) return { ai: false, palette: glassCapsulePalette(skin) };
  return { ai: true, palette: "" };
}

/** Vertical rail skins — fixed right chrome; bottom bar is prompt-only. */
export function isSideLivingBarSkin(skin: LivingBarSkin): boolean {
  return skin === "side_rail" || skin === "side_theater";
}

export const LIVING_BAR_SKIN_LABELS: Record<LivingBarSkin, string> = {
  ai_styled: "AI / Custom style",
  sticky_notes: "Sticky notes",
  glass: "Glass thought cards",
  constellation: "Constellation",
  radar: "Radar",
  command_line: "Command-line aura",
  split_theater: "Now / Ahead theater",
  on_this_page: "On this page",
  side_rail: "Side rail",
  side_theater: "Theater side rail",
  clinical_glass: "Clinical glass",
  graphite: "Graphite",
  ember: "Ember",
  deep_field: "Deep field",
};

/**
 * Where each style FITS — the site-owner-facing "is this one for me?" line.
 * Distinct from LIVING_BAR_SKIN_BLURBS, which describes what the style LOOKS
 * like: a customer picking a look needs to know which of their pages it
 * suits, not just how it is drawn.
 */
export const LIVING_BAR_SKIN_BEST_FOR: Record<LivingBarSkin, string> = {
  ai_styled: "Sites that should wear their own look — your palette, no chrome of ours.",
  sticky_notes: "Playful, colorful brands — retail, local services, anything informal.",
  glass: "Modern product and SaaS sites with cool, clean palettes.",
  constellation: "Technical or data products that want an exploratory feel.",
  radar: "Security, monitoring and other technical products, especially on dark pages.",
  command_line: "Developer tools and audiences who live in a terminal.",
  split_theater: "Long editorial pages — it teaches what this section covers and what is next.",
  on_this_page: "Documentation and knowledge-heavy pages; the plainest, quietest option.",
  side_rail: "Long pages where a bottom bar would fight a sticky footer or cookie banner.",
  side_theater: "The side rail for dark, cinematic brands.",
  clinical_glass: "Trust-sensitive sites — healthcare, legal, finance — that need a private, quiet feel.",
  graphite: "White, type-led sites with no dark surface anywhere; adds weight without adding color.",
  ember: "Warm brands that want one confident accent — louder than Graphite.",
  deep_field: "Blue-on-blue sites where a tinted bar disappears — the bar reads as emitted light.",
};

export const LIVING_BAR_SKIN_BLURBS: Record<LivingBarSkin, string> = {
  ai_styled:
    "The bar the AI designer drew from your site — your colors, your corners, your wording, and nothing else layered on top.",
  sticky_notes:
    "Paper notes lean out of the bar — playful, multi-line questions with a Get answer chip.",
  glass:
    "Frosted glass cards in a straight dock row — cool blue for this section, violet for coming up.",
  constellation:
    "Star-linked nodes above the dock — questions as a small constellation map of this page.",
  radar:
    "Sweeping radar over the bar — section questions as blips on a pulse scan.",
  command_line:
    "Sleek terminal-style panel — clean monospaced questions, soft Ask chip, no code jargon.",
  split_theater:
    "Two clear stages — Now and Ahead — split by a light seam. Editorial, cinematic, instantly teaches the model.",
  on_this_page:
    "Clean dual panel above the bar — this section (2) + coming up next (2), Answer now chips, Answer me now CTA.",
  side_rail:
    "Same dual-chrome (this section + next) as a vertical rail on the far right; prompt bar stays bottom-only.",
  side_theater:
    "Theater dual-chrome (this section + coming up) as a dark vertical rail on the far right — same behavior as side rail, cinematic look.",
  clinical_glass:
    "Physician-navy glass capsule with floating question chips above it — quiet private-session chrome for trust-sensitive sites. Questions render whole or not at all; no section labels, no truncation.",
  graphite:
    "Neutral charcoal capsule carrying a royal blue — for white, type-led sites that have no dark surface anywhere. Weight is the whole opening.",
  ember:
    "Same capsule, warm graphite ground, and an accent orange spent as the one signal. Louder than Graphite, less literal about the brand.",
  deep_field:
    "Deep navy capsule with an electric blue that reads as emitted light — for sites where everything is already blue and a tinted bar disappears.",
};

/** Pure set membership — safe for snippet bundle (no zod). */
export function isLivingBarSkin(v: unknown): v is LivingBarSkin {
  return typeof v === "string" && (LIVING_BAR_SKINS as readonly string[]).includes(v);
}

export function parseLivingBarSkin(raw: unknown): LivingBarSkin {
  if (typeof raw === "string" && isLivingBarSkin(raw)) {
    return raw;
  }
  // jsonb may arrive already parsed, or as a quoted JSON string value.
  if (raw != null && typeof raw === "object" && "skin" in (raw as object)) {
    const skin = (raw as { skin?: unknown }).skin;
    if (isLivingBarSkin(skin)) return skin;
  }
  return DEFAULT_LIVING_BAR_SKIN;
}
